import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { safeReturnTo } from "../utils/helpSite";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../config/firebase";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Card } from "../components/Card";
import { FormGroup } from "../components/FormGroup";
import { FormLabel } from "../components/FormLabel";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { LogoText } from "@styling/images";
import { useTranslation } from "react-i18next";
import { LanguageToggle } from "../components/LanguageToggle";

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: ${colors.backgroundDark};
  position: relative;
`;

const LangCorner = styled.div`
  position: absolute;
  top: ${spacing.x2}px;
  right: ${spacing.x2}px;
`;

const LoginCard = styled(Card)`
  padding: ${spacing.x4}px;
  width: 100%;
  max-width: 400px;
  @media (max-width: 768px) {
    padding: ${spacing.x3}px;
    margin: ${spacing.x2}px;
    max-width: calc(100% - ${spacing.x2 * 2}px);
  }
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${spacing.x3}px;
`;

const LogoImage = styled.img`
  max-width: 200px;
  width: 100%;
  height: auto;
  object-fit: contain;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x3}px;
`;

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x1}px;
`;

export const LoginView = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    signInWithEmailAndPassword(auth, email, password)
      .then(() => {
        navigate(safeReturnTo(searchParams.get("returnTo")));
      })
      .catch((err) => {
        setError(err.message || t("login.errors.generic"));
        setLoading(false);
      });
  };

  return (
    <LoginContainer>
      <LangCorner>
        <LanguageToggle />
      </LangCorner>
      <LoginCard>
        <LogoContainer>
          <LogoImage src={LogoText} alt={t("login.logoAlt")} />
        </LogoContainer>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <FormLabel>{t("login.email")}</FormLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("login.emailPlaceholder")}
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>{t("login.password")}</FormLabel>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("login.passwordPlaceholder")}
              required
            />
          </FormGroup>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <Button type="submit" disabled={loading}>
            {loading ? t("login.signingIn") : t("login.signIn")}
          </Button>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

